import React from 'react';

import { useIntl } from 'react-intl';

import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import InfoIcon from '@atlaskit/icon/core/status-information';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useFireOAuthHovered } from '../../../controllers/events/use-fire-oauth-events';
import { isConnectorSearchableWithoutUserOAuth } from '../../utils/connectors';
import { useBootstrap } from '../../../controllers/store/bootstrap';
import { type NounKeys } from '../../constants/nouns';
import { type ProductKeys } from '../../constants/product-keys';
import { useProductConfigs } from '../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import type { TriggerComponentProps } from '../../types';
import type { OAuthSource } from '../../utils/events/oauth-events';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../utils/experiments';
import { getWorkspaceNameFromProduct } from '../../utils/get-workspace-name-from-product';
import type { FilterOption } from '../filters/app-filter-sidebar/show-more-apps-popup';

import { messages } from './messages';

interface OAuthHandlersProps {
	product: ProductKeys | NounKeys | undefined;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	query?: string;
	datasourceId?: string;
}

interface OAuthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

export type UseOAuthHandlers = (props: OAuthHandlersProps) => OAuthHandlerFunctions;

export type UseIsUserOAuthed = (
	productKey: ProductKeys | NounKeys | undefined,
	datasourceId?: string | undefined,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  };

type OAuthWarningWrapperProps = {
	product: ProductKeys | NounKeys | undefined;
	isSmartlink: boolean;
	isInline?: boolean;
	trigger: (props: TriggerComponentProps) => JSX.Element;
	isOnSearchDialog?: boolean;
	searchQuery?: string;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	option?: FilterOption;
	index?: number;
	postAuthAction?: () => void;
	oAuthSource: OAuthSource;
	datasourceId?: string;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	skipFlagsProvider?: boolean;
	onAppFilterClick?: () => void;
};

const infoIconStyles = xcss({
	marginLeft: 'space.050',
	cursor: 'pointer',
});
const popupTriggerStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});
const connectButtonStyles = xcss({
	marginLeft: 'auto',
	right: 'space.200',
	display: 'flex',
	flexDirection: 'column',
});
const sidebarStyles = xcss({
	width: '100%',
});
const connectTextStyles = xcss({
	color: 'color.link',
	font: 'font.body',
	fontWeight: 'font.weight.medium',

	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

const singleSelectConnectTextStyles = xcss({
	color: 'color.link',
	font: 'font.body.small',
	fontWeight: 'font.weight.medium',

	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

export const OAuthWarningWrapperImpl = ({
	product,
	isInline,
	trigger,
	isOnSearchDialog,
	searchQuery,
	useOAuthHandlers,
	useIsUserOAuthed,
	option,
	postAuthAction,
	oAuthSource,
	datasourceId,
	thirdPartyConfigs,
	onAppFilterClick,
}: OAuthWarningWrapperProps): React.JSX.Element => {
	const products = useProductConfigs();
	const [{ cloudId }] = useBootstrap();
	const { formatMessage } = useIntl();
	const { userNeedsOAuth, outboundAuthUrl } = useIsUserOAuthed(product, datasourceId);
	const fireOAuthHovered = useFireOAuthHovered({
		cloudId: cloudId || '',
		product: product || '',
		isOnSearchDialog,
		thirdPartyConfigs: thirdPartyConfigs || {},
	});

	// For Forge connectors, use workspaceName from thirdPartyConfigs
	const workspaceName = thirdPartyConfigs
		? getWorkspaceNameFromProduct(product, thirdPartyConfigs)
		: undefined;
	const productString =
		(product && workspaceName) || (product && products[product as ProductKeys]?.displayName) || '';
	const { onAuthorize } = useOAuthHandlers({
		product,
		successFlagTitle: formatMessage(messages.authorizationSuccess, {
			product: productString,
		}),
		failFlagTitle: formatMessage(messages.authorizationFail, {
			product: productString,
		}),
		outboundAuthUrl: outboundAuthUrl || '',
		onCompletedAction: () => {
			option?.showFilter();
			option?.onSelect();
			postAuthAction?.();
		},
		isOnSearchDialog,
		query: searchQuery,
		datasourceId,
	});

	// This component is a functional wrapper that turns the trigger prop into a connect trigger.
	// if no warning is needed, we just render the trigger
	const isAdminImported = isConnectorSearchableWithoutUserOAuth(product, thirdPartyConfigs ?? {});
	if (!product || !userNeedsOAuth || !outboundAuthUrl || isAdminImported) {
		return trigger({ isDisabled: false });
	}

	// One-click 3P connect: authorize immediately when the user clicks the trigger.
	const handleClickConnect = () => {
		onAppFilterClick?.();
		onAuthorize(oAuthSource);
	};

	const handleConnectHover = () => {
		setTimeout(() => {
			fireOAuthHovered(oAuthSource);
		}, 1000);
	};

	const connectButton = () => {
		return (
			<Box xcss={connectButtonStyles} onMouseEnter={handleConnectHover}>
				<Box
					xcss={
						getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
							? singleSelectConnectTextStyles
							: connectTextStyles
					}
				>
					{formatMessage(messages.connect)}
				</Box>
			</Box>
		);
	};

	const oAuthWarning = isInline ? (
		<Box xcss={infoIconStyles}>
			<InfoIcon label="oauth warning" color={token('color.icon.brand')} size="small" />
		</Box>
	) : (
		<>{connectButton()}</>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			onClick={handleClickConnect}
			onMouseEnter={handleConnectHover}
			xcss={[popupTriggerStyles, !isInline && !isOnSearchDialog && sidebarStyles]}
		>
			{trigger({ oAuthWarning, isDisabled: true })}
		</Box>
	);
};

export const OAuthWarningWrapper = (props: OAuthWarningWrapperProps): React.JSX.Element => {
	return props.isOnSearchDialog || props.skipFlagsProvider ? (
		<OAuthWarningWrapperImpl {...props} />
	) : (
		<FlagsProvider>
			<OAuthWarningWrapperImpl {...props} />
		</FlagsProvider>
	);
};
